import type { ReservationConfig, Store } from '../store/useStore'
import { isYokosoPointsEnabled } from './features'

/**
 * #2026 予約で**実際に使われる**支払い方法とキャンセル料保証。客に見せる規定・注意書き・
 * ボタンの表記・予約の締切はこれで出す。
 *
 * 現金のみ運用の店は、保存されている予約の設定 (前払い・手付金・保証) に関わらず当日払い
 * (保証なし) になる。サーバは予約を作るときに同じ規則で当日払いに固定している
 * (`payment_timing_resolver.resolve_payment_timing` の cash_only)。保存値をそのまま読むと、
 * 当日払いにしかならない店で前払いの返金表や「お支払い情報入力」が出る。
 *
 * 保存値は消さない。現金のみを OFF に戻したとき、元の設定に戻すため。
 */
export function reservationPaymentRules(
  store: Pick<Store, 'cashOnlyMode' | 'reservationConfig'> | null | undefined,
): { paymentTiming: NonNullable<ReservationConfig['paymentTiming']>; noShowProtection: boolean } {
  const cashOnly = store?.cashOnlyMode === true
  const config = store?.reservationConfig
  return {
    paymentTiming: cashOnly ? 'onsite' : (config?.paymentTiming ?? 'onsite'),
    noShowProtection: !cashOnly && !!config?.noShowProtectionEnabled,
  }
}

/**
 * 客が店で使える支払手段 (#972 / #1438 / #1769)。StoreDetail・QRScan・店舗設定で
 * 同じ表を使う (別々に書くと「詳細ではポイント可、支払画面では不可」がすぐ起きる)。
 *
 * - kycOk: owner の Stripe 本人確認 (chargesEnabled)。backend が `paymentEnabled` として
 *   `/stores/{id}` と `/stores/nearby` に付ける。無い (旧クライアント / fallback の store
 *   object) ときは従来どおり OK 扱い — 境界は backend (`/payment/execute`) が持つ
 * - cardInApp: アプリ内カード決済。KYC 済みで「現金のみ運用」でない店だけ
 * - points: YOKOSO ポイント払い。KYC 済み (= 送金先の口座がある) で、店が断っていない
 *   (`acceptsPoints !== false`) こと。**現金のみ運用でも可** (口座はあるので入金できる)
 * - cash: 現金会計。KYC 済み or 現金のみ運用 (= 一覧に載る店)
 */
export function storePaymentOptions(store: Pick<Store, 'paymentEnabled' | 'cashOnlyMode' | 'acceptsPoints'> | null | undefined) {
  const kycOk = store?.paymentEnabled !== false
  const cashOnly = store?.cashOnlyMode === true
  return {
    kycOk,
    cashOnly,
    cardInApp: kycOk && !cashOnly,
    points: kycOk && store?.acceptsPoints !== false && isYokosoPointsEnabled(),
    cash: kycOk || cashOnly,
  }
}

// ---------------------------------------------------------------------------
// #2162 店頭の会計の支払手段 (backend: services/store_payment.py の定義と同じ)
// 'cash' = 現金、'store_terminal' = 店の端末 (STORES 決済・Airペイ・Square など他社の決済端末で
// 受けた会計の記録だけ。YOKOSO PAY は端末と連携しない)。会社名はシステム上区別しない
// ---------------------------------------------------------------------------
export type RegisterPayMethod = 'cash' | 'store_terminal'

export const PAYMENT_METHOD_STORE_TERMINAL = 'store_terminal'

/** レジで「店の端末で受領」を出すか (店の設定で ON にした店だけ) */
export const storeTerminalEnabled = (store: { storeTerminalEnabled?: boolean } | null | undefined): boolean =>
  store?.storeTerminalEnabled === true

/** 店頭で払った (YOKOSO PAY の中でお金が動かない) 支払手段か。現金と店の端末 */
export const paidInPerson = (method: string | null | undefined): boolean =>
  method === 'cash' || method === PAYMENT_METHOD_STORE_TERMINAL

/** #2162 押し直しで支払手段が違った (同じ会計が別の支払手段で記録済み)。サーバの 409 */
export const isAlreadyRecordedOther = (e: unknown): boolean =>
  (e as { code?: string } | null)?.code === 'already_recorded_other_method'
